import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import '../CSS/LeaveApplication.css';
import { applyLeave } from '../features/leave/leaveThunks';
import { clearApplySuccess } from '../features/leave/leaveSlice';
import Sidebar from './Sidebar';

const LeaveApplication = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isApplying, applyError, applySuccess } = useSelector(
    (state) => state.leave
  );

  const [formData, setFormData] = useState({
    leave_type:    'CASUAL',
    planning_type: 'PLANNED',
    duration:      'FULL_DAY',
    fromDate:      '',
    toDate:        '',
    reason:        '',
  });
  const [errors, setErrors] = useState({});

  const leaveTypes = [
    { value: 'CASUAL',  label: 'Casual Leave' },
    { value: 'MEDICAL', label: 'Medical Leave' },
    { value: 'EARNED',  label: 'Earned Leave' },
  ];

  const planningTypes = [
    { value: 'PLANNED',     label: 'Planned Leave (at least 2 days before)' },
    { value: 'UNPLANNED',   label: 'Unplanned Leave' },
    { value: 'LAST_MINUTE', label: 'Last Minute Leave' },
  ];

  const durations = [
    { value: 'FULL_DAY', label: 'Full Day' },
    { value: 'HALF_DAY', label: 'Half Day' },
  ];

  const isHalfDay = formData.duration === 'HALF_DAY';

  useEffect(() => {
    if (isHalfDay && formData.fromDate && formData.toDate !== formData.fromDate) {
      setFormData((prev) => ({ ...prev, toDate: prev.fromDate }));
    }
  }, [isHalfDay, formData.fromDate, formData.toDate]);

  useEffect(() => {
    if (applySuccess) {
      setTimeout(() => {
        dispatch(clearApplySuccess());
        navigate('/employee/leave/status');
      }, 2000);
    }
  }, [applySuccess, dispatch, navigate]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const calculateLeaveDays = () => {
    if (!formData.fromDate) return 0;
    const toDate = isHalfDay ? formData.fromDate : formData.toDate;
    if (!toDate) return 0;

    const from = new Date(formData.fromDate);
    const to   = new Date(toDate);
    const days = Math.ceil(Math.abs(to - from) / (1000 * 60 * 60 * 24)) + 1;
    return isHalfDay ? days * 0.5 : days;
  };

  const getMinDate = () => {
    const today = new Date();
    if (formData.planning_type === 'PLANNED') {
      today.setDate(today.getDate() + 2);
    }
    return today.toISOString().split('T')[0];
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fromDate)            newErrors.fromDate = 'From date is required';
    if (!isHalfDay && !formData.toDate) newErrors.toDate   = 'To date is required';
    if (!formData.reason.trim())        newErrors.reason   = 'Reason is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // The backend now auto-resolves the employee from the authenticated JWT token.
    // We no longer need to send employee_id — doing so for privileged users is handled
    // server-side; regular employees simply don't need to include it.
    const payload = {
      leave_type:    formData.leave_type,
      planning_type: formData.planning_type,
      duration:      formData.duration,
      from_date:     formData.fromDate,
      to_date:       isHalfDay ? formData.fromDate : formData.toDate,
      reason:        formData.reason,
    };

    try {
      await dispatch(applyLeave(payload)).unwrap();
      setFormData({
        leave_type:    'CASUAL',
        planning_type: 'PLANNED',
        duration:      'FULL_DAY',
        fromDate:      '',
        toDate:        '',
        reason:        '',
      });
    } catch (error) {
      setErrors({ submit: error || 'Failed to submit leave application' });
    }
  };

  return (
    <div className='index-box'>
      <Sidebar />
      <div className="leave-app-container">
        <div className="leave-app-header">
          <div className="leave-app-header-content">
            <h2 className="leave-app-title">Apply for Leave</h2>
            <p className="leave-app-subtitle">
              Submit your leave request for approval
            </p>
          </div>
        </div>

        {applySuccess && (
          <div className="leave-app-alert leave-app-alert-success">
            <div className="leave-app-alert-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 6L9 17l-5-5"></path>
              </svg>
            </div>
            <div className="leave-app-alert-content">
              Leave application submitted successfully! Awaiting Department Head approval.
            </div>
          </div>
        )}

        {(applyError || errors.submit) && (
          <div className="leave-app-alert leave-app-alert-error">
            <div className="leave-app-alert-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <div className="leave-app-alert-content">
              {applyError || errors.submit}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="leave-app-form">
          <div className="leave-app-form-section">
            <h3 className="leave-app-form-section-title">Leave Details</h3>
            
            <div className="leave-app-form-row">
              <div className="leave-app-form-field">
                <label htmlFor="leave_type" className="leave-app-form-label">
                  Leave Type
                  <span className="leave-app-form-required">*</span>
                </label>
                <select
                  id="leave_type"
                  value={formData.leave_type}
                  onChange={(e) => handleInputChange('leave_type', e.target.value)}
                  className="leave-app-form-select"
                >
                  {leaveTypes.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div className="leave-app-form-field">
                <label htmlFor="planning_type" className="leave-app-form-label">
                  Planning Type
                  <span className="leave-app-form-required">*</span>
                </label>
                <select
                  id="planning_type"
                  value={formData.planning_type}
                  onChange={(e) => handleInputChange('planning_type', e.target.value)}
                  className="leave-app-form-select"
                >
                  {planningTypes.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="leave-app-form-row">
              <div className="leave-app-form-field">
                <label htmlFor="duration" className="leave-app-form-label">
                  Duration
                  <span className="leave-app-form-required">*</span>
                </label>
                <select
                  id="duration"
                  value={formData.duration}
                  onChange={(e) => handleInputChange('duration', e.target.value)}
                  className="leave-app-form-select"
                >
                  {durations.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>

              <div className="leave-app-form-field">
                <label htmlFor="fromDate" className="leave-app-form-label">
                  {isHalfDay ? 'Date' : 'From Date'}
                  <span className="leave-app-form-required">*</span>
                </label>
                <input
                  type="date"
                  id="fromDate"
                  value={formData.fromDate}
                  onChange={(e) => handleInputChange('fromDate', e.target.value)}
                  min={getMinDate()}
                  className={`leave-app-form-input ${errors.fromDate ? 'leave-app-form-input-error' : ''}`}
                />
                {errors.fromDate && (
                  <span className="leave-app-form-error-text">{errors.fromDate}</span>
                )}
              </div>
            </div>

            {!isHalfDay && (
              <div className="leave-app-form-row">
                <div className="leave-app-form-field">
                  <label htmlFor="toDate" className="leave-app-form-label">
                    To Date
                    <span className="leave-app-form-required">*</span>
                  </label>
                  <input
                    type="date"
                    id="toDate"
                    value={formData.toDate}
                    onChange={(e) => handleInputChange('toDate', e.target.value)}
                    min={formData.fromDate || getMinDate()}
                    className={`leave-app-form-input ${errors.toDate ? 'leave-app-form-input-error' : ''}`}
                  />
                  {errors.toDate && (
                    <span className="leave-app-form-error-text">{errors.toDate}</span>
                  )}
                </div>
              </div>
            )}

            {formData.fromDate && (isHalfDay || formData.toDate) && (
              <div className="leave-app-days-summary">
                <div className="leave-app-days-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </div>
                <span className="leave-app-days-text">
                  Total Leave Days: <strong>{calculateLeaveDays()}</strong>
                </span>
              </div>
            )}
          </div>

          <div className="leave-app-form-section">
            <h3 className="leave-app-form-section-title">Reason</h3>
            
            <div className="leave-app-form-field">
              <label htmlFor="reason" className="leave-app-form-label">
                Provide detailed reason for your leave
                <span className="leave-app-form-required">*</span>
              </label>
              <textarea
                id="reason"
                rows="5"
                value={formData.reason}
                onChange={(e) => handleInputChange('reason', e.target.value)}
                placeholder="Please provide a detailed reason for your leave request..."
                className={`leave-app-form-textarea ${errors.reason ? 'leave-app-form-input-error' : ''}`}
              ></textarea>
              {errors.reason && (
                <span className="leave-app-form-error-text">{errors.reason}</span>
              )}
            </div>
          </div>

          <div className="leave-app-form-actions">
            <button 
              type="submit" 
              className="leave-app-submit-btn" 
              disabled={isApplying}
            >
              {isApplying ? (
                <>
                  <span className="leave-app-submit-spinner"></span>
                  Submitting...
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                  Submit Leave Request
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeaveApplication;